import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Phone,
  LocationOn,
  Work,
  Google,
  Facebook,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { motion } from 'framer-motion';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { showError, showSuccess } = useNotification();
  
  const [activeStep, setActiveStep] = useState(0);
  const [userType, setUserType] = useState(searchParams.get('type') || 'client');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: searchParams.get('type') || 'client',
    acceptTerms: false,
    acceptPrivacy: false,
    
    // Location
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Professional Info (only for professionals)
    businessName: '',
    services: [],
    experience: '',
    description: '',
    hourlyRate: '',
  });

  const steps = userType === 'professional' 
    ? ['Información Básica', 'Ubicación', 'Información Profesional']
    : ['Información Básica', 'Ubicación'];

  const availableServices = [
    'Limpieza del Hogar',
    'Plomería',
    'Electricidad',
    'Jardinería',
    'Pintura',
    'Carpintería',
    'Reparaciones Generales',
    'Instalaciones',
    'Mantenimiento',
    'Decoración',
  ];

  const states = [
    'Bogotá D.C.',
    'Antioquia',
    'Valle del Cauca',
    'Cundinamarca',
    'Atlántico',
    'Santander',
    'Bolívar',
    'Norte de Santander',
    'Córdoba',
    'Tolima',
  ];

  useEffect(() => {
    setFormData(prev => ({ ...prev, userType }));
  }, [userType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleServicesChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      services: newValue,
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      // Basic Info validation
      if (!formData.name.trim()) {
        newErrors.name = 'El nombre es requerido';
      }
      
      if (!formData.email) {
        newErrors.email = 'El email es requerido';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'El email no es válido';
      }
      
      if (!formData.phone) {
        newErrors.phone = 'El teléfono es requerido';
      } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'El teléfono debe tener 10 dígitos';
      }
      
      if (!formData.password) {
        newErrors.password = 'La contraseña es requerida';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirma tu contraseña';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
      
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
      }
      
      if (!formData.acceptPrivacy) {
        newErrors.acceptPrivacy = 'Debes aceptar la política de privacidad';
      }
    }
    
    if (step === 1) {
      // Location validation
      if (!formData.address.trim()) {
        newErrors.address = 'La dirección es requerida';
      }
      
      if (!formData.city.trim()) {
        newErrors.city = 'La ciudad es requerida';
      }
      
      if (!formData.state) {
        newErrors.state = 'El estado es requerido';
      }
      
      if (!formData.zipCode) {
        newErrors.zipCode = 'El código postal es requerido';
      } else if (!/^[0-9]{5}$/.test(formData.zipCode)) {
        newErrors.zipCode = 'El código postal debe tener 5 dígitos';
      }
    }
    
    if (step === 2 && userType === 'professional') {
      // Professional Info validation
      if (!formData.businessName.trim()) {
        newErrors.businessName = 'El nombre del negocio es requerido';
      }
      
      if (formData.services.length === 0) {
        newErrors.services = 'Selecciona al menos un servicio';
      }
      
      if (!formData.experience) {
        newErrors.experience = 'La experiencia es requerida';
      }
      
      if (!formData.description.trim()) {
        newErrors.description = 'La descripción es requerida';
      }
      
      if (!formData.hourlyRate) {
        newErrors.hourlyRate = 'La tarifa por servicio es requerida';
      } else if (isNaN(formData.hourlyRate) || formData.hourlyRate <= 0) {
        newErrors.hourlyRate = 'Ingresa una tarifa válida';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(activeStep)) {
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await register(formData);
      
      if (result.success) {
        showSuccess('¡Registro exitoso! Bienvenido a Jooru');
        navigate('/dashboard');
      } else {
        showError(result.message || 'Error al registrarse');
      }
    } catch (error) {
      console.error('Registration error:', error);
      showError('Error inesperado al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = (provider) => {
    // TODO: Implement social registration
    showError(`Registro con ${provider} no está disponible aún`);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                Tipo de Usuario
              </FormLabel>
              <RadioGroup
                row
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
              >
                <FormControlLabel
                  value="client"
                  control={<Radio />}
                  label="Cliente"
                  sx={{ mr: 4 }}
                />
                <FormControlLabel
                  value="professional"
                  control={<Radio />}
                  label="Profesional"
                />
              </RadioGroup>
            </FormControl>
            
            <TextField
              fullWidth
              name="name"
              label="Nombre Completo"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              name="phone"
              label="Teléfono"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              name="confirmPassword"
              label="Confirmar Contraseña"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Acepto los{' '}
                    <Link href="#" color="primary" sx={{ textDecoration: 'none' }}>
                      términos y condiciones
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'flex-start' }}
              />
              {errors.acceptTerms && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.acceptTerms}
                </Typography>
              )}
              
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptPrivacy"
                    checked={formData.acceptPrivacy}
                    onChange={(e) => setFormData(prev => ({ ...prev, acceptPrivacy: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Acepto la{' '}
                    <Link href="#" color="primary" sx={{ textDecoration: 'none' }}>
                      política de privacidad
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'flex-start' }}
              />
              {errors.acceptPrivacy && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.acceptPrivacy}
                </Typography>
              )}
            </Box>
          </Box>
        );
        
      case 1:
        return (
          <Box>
            <TextField
              fullWidth
              name="address"
              label="Dirección"
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                name="city"
                label="Ciudad"
                value={formData.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
                margin="normal"
              />
              
              <Autocomplete
                fullWidth
                options={states}
                value={formData.state}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, state: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="state"
                    label="Estado"
                    error={!!errors.state}
                    helperText={errors.state}
                    margin="normal"
                  />
                )}
              />
            </Box>
            
            <TextField
              fullWidth
              name="zipCode"
              label="Código Postal"
              value={formData.zipCode}
              onChange={handleChange}
              error={!!errors.zipCode}
              helperText={errors.zipCode}
              margin="normal"
            />
          </Box>
        );
        
      case 2:
        return (
          <Box>
            <TextField
              fullWidth
              name="businessName"
              label="Nombre del Negocio"
              value={formData.businessName}
              onChange={handleChange}
              error={!!errors.businessName}
              helperText={errors.businessName}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Work color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Autocomplete
              multiple
              fullWidth
              options={availableServices}
              value={formData.services}
              onChange={handleServicesChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Servicios que Ofreces"
                  error={!!errors.services}
                  helperText={errors.services}
                  margin="normal"
                />
              )}
            />
            
            <TextField
              fullWidth
              name="experience"
              label="Años de Experiencia"
              type="number"
              value={formData.experience}
              onChange={handleChange}
              error={!!errors.experience}
              helperText={errors.experience}
              margin="normal"
              inputProps={{ min: 0, max: 50 }}
            />
            
            <TextField
              fullWidth
              name="hourlyRate"
              label="Tarifa por Servicio (COP)"
              type="number"
              value={formData.hourlyRate}
              onChange={handleChange}
              error={!!errors.hourlyRate}
              helperText={errors.hourlyRate}
              margin="normal"
              inputProps={{ min: 0, step: 10 }}
            />
            
            <TextField
              fullWidth
              name="description"
              label="Descripción de tus Servicios"
              multiline
              rows={4}
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              margin="normal"
              placeholder="Describe tu experiencia, especialidades y qué te hace único..."
            />
          </Box>
        );
        
      default:
        return null;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Paper
            elevation={10}
            sx={{
              p: 4,
              borderRadius: 3,
              backgroundColor: 'white',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 'bold', color: 'primary.main' }}
              >
                Jooru
              </Typography>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Crear Cuenta
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Únete a nuestra comunidad de profesionales y clientes
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box component="form" onSubmit={handleSubmit}>
              {renderStepContent(activeStep)}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  startIcon={<ArrowBack />}
                  sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
                >
                  Anterior
                </Button>
                
                {isLastStep ? (
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Crear Cuenta'
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    endIcon={<ArrowForward />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                    }}
                  >
                    Siguiente
                  </Button>
                )}
              </Box>
            </Box>

            {activeStep === 0 && (
              <>
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    O regístrate con
                  </Typography>
                </Divider>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Google />}
                    onClick={() => handleSocialRegister('Google')}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Google
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Facebook />}
                    onClick={() => handleSocialRegister('Facebook')}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Facebook
                  </Button>
                </Box>
              </>
            )}

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                ¿Ya tienes una cuenta?{' '}
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    color: 'primary.main',
                  }}
                >
                  Inicia sesión aquí
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Register;